import React, { useState, useMemo } from "react";
import { useParams, Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useHelpdeskNavigation } from "../hooks/useHelpdeskNavigation";
import { useTicket, useUpdateTicket, useAddComment } from "../hooks/useTickets";
import { useCategories } from "../hooks/useCategories";
import { useAssignableUsers } from "../hooks/useUsers";
import { useDateFormat } from "../hooks/useDateFormat";
import { useSignalR } from "../contexts/SignalRContext";
import { queryKeys } from "../lib/queryKeys";
import { OrganizationBadge } from "../components/OrganizationBadge";
import type { UpdateTicketRequest, AddCommentRequest, TicketAttachmentResponse } from "../types/tickets";

// SignalR event data types
interface TicketUpdatedData {
  ticketId?: string;
  TicketId?: string;
  changes?: string[];
  updatedBy?: string;
}

interface ParticipantsChangedData {
  ticketId?: string;
  changeType?: 'added' | 'removed';
  participant?: {
    customerId?: string;
    customerName?: string;
    customerEmail?: string;
  };
}

interface CommentAddedData {
  ticketId?: string;
  TicketId?: string;
  commentId?: string;
  CommentId?: string;
  author?: string;
  authorName?: string;
  AuthorName?: string;
  attachments?: unknown[];
  Attachments?: unknown[];
}

interface CommentAttachmentAddedData {
  ticketId?: string;
  TicketId?: string;
  FileName?: string;
  fileName?: string;
}

interface FollowersChangedData {
  ticketId?: string;
  changeType?: 'added' | 'removed';
  follower?: {
    userId?: string;
    userName?: string;
  };
}
import {
  TicketStatus,
  Priority,
  TicketStatusLabels,
  PriorityLabels,
  PriorityColors,
  TicketStatusColors,
} from "../types/tickets";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SplitButton } from "../components/ui/split-button";
import { TreeSelect } from "../components/tree-select/TreeSelect";
import { activeCategoryOptions } from "../components/tree-select/treeOptions";
import { DateTimePicker } from "../components/ui/date-time-picker";
import { FileUpload } from "../components/FileUpload";
import { AttachmentItem } from "../components/AttachmentItem";
import { TicketHistory } from "../components/TicketHistory";
import { TicketProgressBar } from "../components/TicketProgressBar";
import { TicketDetailSkeleton } from "../components/LoadingSkeleton";
import { TicketParticipants } from "../components/tickets/TicketParticipants";
import { TicketArticleLinks } from "../components/tickets/TicketArticleLinks";
import { TicketFollowButton } from "../components/tickets/TicketFollowButton";
import { TicketMergeDialog } from "../components/tickets/TicketMergeDialog";
import { TicketTrashDialog } from "../components/tickets/TicketTrashDialog";
import { RedactTextDialog } from "../components/tickets/RedactTextDialog";
import { AttachmentDeleteDialog } from "../components/tickets/AttachmentDeleteDialog";
import { useAdminOrganizations } from "../hooks/useAdminOrganizations";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { apiService } from "../services/api";
import { showToast } from "../lib/toast";
import { getErrorMessage } from "../lib/apiErrors";
import { formatDuration } from "../lib/dateUtils";
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  FileText,
  MessageSquare,
  User,
  AlertCircle,
  CircleDot,
  Hourglass,
  PauseCircle,
  CheckCircle,
  XCircle,
  ArrowDown,
  ArrowUp,
  Minus,
  AlertTriangle,
  Calendar,
  RefreshCw,
  Signal,
  FolderOpen,
  Clock,
  Activity,
  GitMerge,
  MoreVertical,
  Trash2,
  EyeOff,
} from "lucide-react";

// The server keeps a comment even when one of its attachments is rejected, so compare what
// was saved against what was sent instead of assuming every file made it.
function missingAttachmentsNote(sent: number, saved: number): string | null {
  const missing = sent - saved;
  if (missing <= 0) return null;
  return `${missing} of ${sent} attachment${sent === 1 ? "" : "s"} couldn't be uploaded`;
}

export function TicketDetail() {
  const { id } = useParams({ from: "/helpdesk/tickets/$id" });
  const { getPath, navigateTo } = useHelpdeskNavigation();
  const queryClient = useQueryClient();

  // TanStack Query hooks
  const { data: ticket, isLoading, error, isError } = useTicket(id);
  const updateTicketMutation = useUpdateTicket();
  const addCommentMutation = useAddComment();
  const { formatDateTime, formatDateForInput, parseInputToUTC } =
    useDateFormat();

  // SignalR for real-time updates
  const { signalR, isConnected } = useSignalR();

  // Data queries - pass ticket's organizationId to get correct org-specific data
  const { data: categories = [], error: categoriesError } = useCategories(ticket?.organizationId);
  const { data: users = [], error: usersError } = useAssignableUsers(ticket?.organizationId);

  // Picker options include the ticket's current category even if it has been archived - the picker only offers active
  // categories, and the ticket keeps an archived one until someone changes it
  const categoryOptions = useMemo(
    () => activeCategoryOptions(Array.isArray(categories) ? categories : [], ticket?.category?.id, ticket?.category),
    [categories, ticket?.category]
  );

  // The organization requires a category before staff resolve a ticket
  const categoryRequiredToResolve = !!ticket?.categoryRequiredToResolve;
  const resolveBlockedByCategory = categoryRequiredToResolve && !ticket?.category;

  // Build dropdown options that include the current assignee even if they're no longer assignable
  const assigneeOptions = useMemo(() => {
    const usersList = Array.isArray(users) ? users : [];
    const options = usersList.map(user => ({
      id: user.id,
      fullName: user.fullName,
      isAssignable: true
    }));

    // If ticket has an assignee who is not in the assignable users list, add them with indicator
    if (ticket?.assignedTo) {
      const isCurrentAssigneeInList = usersList.some(u => u.id === ticket.assignedTo?.id);
      if (!isCurrentAssigneeInList) {
        options.unshift({
          id: ticket.assignedTo.id,
          fullName: `${ticket.assignedTo.fullName} (Not Assignable)`,
          isAssignable: false
        });
      }
    }

    return options;
  }, [users, ticket?.assignedTo]);

  // Edit mode states - initialize with empty defaults, will be set by useEffect
  const [editMode, setEditMode] = useState(false);
  const [editSubject, setEditSubject] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editStatus, setEditStatus] = useState<TicketStatus>(TicketStatus.Open);
  const [editPriority, setEditPriority] = useState<Priority>(Priority.Medium);
  const [editCategoryId, setEditCategoryId] = useState<string>("");
  const [editAssignedToId, setEditAssignedToId] = useState<string>("");
  const [editDueAt, setEditDueAt] = useState<string>("");

  // Merge state
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [trashDialogOpen, setTrashDialogOpen] = useState(false);
  // Deleting a ticket or an attachment and redacting text are for Admins of the ticket's organization
  const { data: adminOrgs } = useAdminOrganizations();
  const isTicketOrgAdmin = (adminOrgs ?? []).some((org) => org.id === ticket?.organizationId);
  // The comment (or, without a commentId, the description) whose text is being redacted
  const [redactTarget, setRedactTarget] = useState<{ commentId?: string; content: string } | null>(null);
  const [attachmentToDelete, setAttachmentToDelete] = useState<TicketAttachmentResponse | null>(null);

  // Comment state
  const [newComment, setNewComment] = useState("");
  const [isInternalComment, setIsInternalComment] = useState(false);
  const [commentAttachments, setCommentAttachments] = useState<File[]>([]);
  const [fileUploadKey, setFileUploadKey] = useState("fileupload-0"); // Key to force FileUpload re-render

  // Validation errors for edit mode
  const [editErrors, setEditErrors] = useState<{
    subject?: string;
    description?: string;
  }>({});

  // Update edit state when ticket data changes, but only when NOT in edit mode
  React.useEffect(() => {
    if (ticket && !editMode) {
      setEditSubject(ticket.subject || "");
      setEditDescription(ticket.description || "");
      setEditStatus(ticket.status);
      setEditPriority(ticket.priority);
      // Handle null/undefined category and assignedTo more explicitly
      setEditCategoryId(ticket.category?.id ?? "");
      setEditAssignedToId(ticket.assignedTo?.id ?? "");
      // Convert dueAt to datetime-local format if it exists (local timezone for input)
      setEditDueAt(ticket.dueAt ? formatDateForInput(ticket.dueAt) : "");
    }
  }, [ticket, formatDateForInput, editMode]);

  // SignalR real-time updates - set up once when component mounts
  React.useEffect(() => {
    if (!isConnected) return;

    const handleTicketUpdate = (data: TicketUpdatedData) => {
      console.log('[SignalR] TicketDetail: Received ticket update:', data);

      // Only handle updates for our current ticket
      const updateTicketId = data.ticketId || data.TicketId;
      if (updateTicketId === id) {
        // Add delay to ensure backend has propagated changes
        setTimeout(() => {
          // Invalidate queries to refresh data
          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.detail(id),
          });

          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.lists(),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.allOrgsLists(),
          });

          // Invalidate history to update Activity History section
          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.history(id),
          });
        }, 250);

        // Toast notifications handled by global InAppNotification handler to avoid duplicates
      }
    };

    const handleCommentAdded = (data: CommentAddedData) => {
      console.log('[SignalR] TicketDetail: Received new comment:', data);

      // Only handle comments for our current ticket
      const commentTicketId = data.ticketId || data.TicketId;
      if (commentTicketId === id) {
        // Add delay to ensure backend has propagated changes
        setTimeout(() => {
          // Invalidate queries to refresh data
          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.detail(id),
          });

          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.lists(),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.allOrgsLists(),
          });

          // Invalidate history to update Activity History section
          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.history(id),
          });
        }, 250);

        // Toast notifications handled by global InAppNotification handler to avoid duplicates
      }
    };

    const handleCommentAttachmentAdded = (data: CommentAttachmentAddedData) => {
      console.log('[SignalR] TicketDetail: Received comment attachment:', data);

      // Only handle attachments for our current ticket
      const attachmentTicketId = data.TicketId || data.ticketId;
      if (attachmentTicketId === id) {
        // Add delay to ensure backend has propagated changes
        setTimeout(() => {
          // Invalidate queries to refresh data
          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.detail(id),
          });

          // Invalidate history to update Activity History section
          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.history(id),
          });
        }, 250);

        // Toast notifications handled by global InAppNotification handler to avoid duplicates
      }
    };

    const handleParticipantsChanged = (data: ParticipantsChangedData) => {
      console.log('[SignalR] TicketDetail: Received participants changed:', data);

      // Only handle changes for our current ticket
      const eventTicketId = data.ticketId;
      if (eventTicketId !== id) {
        return;
      }

      // Invalidate the participants query to refetch the list
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.participants(id),
      });

      // Invalidate ticket detail to update participant count in sidebar
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.detail(id),
      });

      // Invalidate history to update Activity History section
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.history(id),
      });

      // Toast notifications handled by global InAppNotification handler to avoid duplicates
    };

    const handleFollowersChanged = (data: FollowersChangedData) => {
      console.log('[SignalR] TicketDetail: Received followers changed:', data);

      // Only handle changes for our current ticket
      const eventTicketId = data.ticketId;
      if (eventTicketId !== id) {
        return;
      }

      // Invalidate the followers query to refetch the list
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.followers(id),
      });

      // Invalidate history to update Activity History section
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.history(id),
      });

      // Toast notifications handled by global InAppNotification handler to avoid duplicates
    };

    // A merge changes two tickets at once. Refresh whichever of them this page is
    // showing - the source gains its merged banner, the target gains the conversation.
    // Deliberately no auto-navigation: an agent reading the source shouldn't be
    // yanked to another page mid-sentence.
    const handleTicketMerged = (data: { sourceTicketId: string; targetTicketId: string }) => {
      if (data.sourceTicketId !== id && data.targetTicketId !== id) return;

      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.history(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.participants(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.followers(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.articleLinks(id) });
    };

    // Set up event handlers - use cleanup functions returned by on()
    const cleanupTicketUpdated = signalR.on('TicketUpdated', handleTicketUpdate);
    const cleanupCommentAdded = signalR.on('CommentAdded', handleCommentAdded);
    const cleanupCommentAttachmentAdded = signalR.on('CommentAttachmentAdded', handleCommentAttachmentAdded);
    const cleanupParticipantsChanged = signalR.on('ParticipantsChanged', handleParticipantsChanged);
    const cleanupFollowersChanged = signalR.on('FollowersChanged', handleFollowersChanged);
    const cleanupTicketMerged = signalR.on('TicketMerged', handleTicketMerged);

    // Cleanup function - use the cleanup functions returned by on()
    return () => {
      cleanupTicketUpdated();
      cleanupCommentAdded();
      cleanupCommentAttachmentAdded();
      cleanupParticipantsChanged();
      cleanupFollowersChanged();
      cleanupTicketMerged();
    };
  }, [isConnected, signalR, queryClient, id]);

  // Separate effect for joining/leaving ticket groups when ticket loads
  React.useEffect(() => {
    if (!ticket || !isConnected) return;

    // Join the ticket group for real-time updates
    signalR.joinTicket(ticket.id);
    
    // Let others know we're viewing this ticket
    signalR.viewingTicket(ticket.id);

    // Cleanup function
    return () => {
      signalR.leaveTicket(ticket.id);
    };
  }, [ticket, isConnected, signalR]);

  // Ensure edit state is fresh when entering edit mode
  const handleEditModeToggle = () => {
    if (!editMode && ticket) {
      // Entering edit mode - refresh all edit state with current ticket values
      setEditSubject(ticket.subject || "");
      setEditDescription(ticket.description || "");
      setEditStatus(ticket.status);
      setEditPriority(ticket.priority);
      setEditCategoryId(ticket.category?.id ?? "");
      setEditAssignedToId(ticket.assignedTo?.id ?? "");
      setEditDueAt(ticket.dueAt ? formatDateForInput(ticket.dueAt) : "");
    }
    setEditMode(!editMode);
  };

  // Validation function for edit mode
  const validateEditForm = (): boolean => {
    const newErrors: { subject?: string; description?: string } = {};

    // Subject validation (match ticket creation validation)
    if (!editSubject.trim()) {
      newErrors.subject = "Subject is required";
    } else if (editSubject.trim().length > 200) {
      newErrors.subject = "Subject must be less than 200 characters";
    }

    // Description validation (match ticket creation validation)
    if (!editDescription.trim()) {
      newErrors.description = "Description is required";
    } else if (editDescription.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    } else if (editDescription.trim().length > 5000) {
      newErrors.description = "Description is too long";
    }

    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Clear errors when user types
  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditSubject(e.target.value);
    if (editErrors.subject) {
      setEditErrors(prev => ({ ...prev, subject: undefined }));
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditDescription(e.target.value);
    if (editErrors.description) {
      setEditErrors(prev => ({ ...prev, description: undefined }));
    }
  };

  // Note: We always include dueAt in updates now to prevent null assignments,
  // but the backend AreDateTimesEqual function handles precision differences

  const handleUpdate = async () => {
    if (!ticket) return;

    // Validate form before submitting
    if (!validateEditForm()) {
      return;
    }

    if (categoryRequiredToResolve && editStatus === TicketStatus.Resolved && editCategoryId === "") {
      showToast.error("Category Required", "Choose a category before resolving this ticket.");
      return;
    }

    // Build update request - always include dueAt to prevent it from being set to null
    const updateRequest: UpdateTicketRequest = {
      subject: editSubject,
      description: editDescription,
      status: editStatus,
      priority: editPriority,
      categoryId: editCategoryId === "" ? null : editCategoryId,
      assignedToId: editAssignedToId === "" ? null : editAssignedToId,
      dueAt: editDueAt === "" ? null : parseInputToUTC(editDueAt),
    };

    // Send the update request
    try {
      await updateTicketMutation.mutateAsync({
        id: ticket.id,
        updates: updateRequest,
      });
      showToast.ticketUpdated(ticket.ticketNumber);
      setEditMode(false);
      setEditErrors({}); // Clear any validation errors
    } catch (err) {
      console.error("Error updating ticket:", err);
      showToast.error("Update Failed", getErrorMessage(err, "Failed to update ticket"));
    }
  };

  // Split button: add comment then change status
  const handleCommentWithStatus = async (statusValue: string) => {
    if (!ticket || !newComment.trim()) return;

    // Stop before the comment is posted - otherwise the comment would go out and the resolve would then fail
    if (parseInt(statusValue) === TicketStatus.Resolved && resolveBlockedByCategory) {
      showToast.error("Category Required", "Choose a category before resolving this ticket.");
      return;
    }

    const commentRequest: AddCommentRequest = {
      content: newComment.trim(),
      isInternal: isInternalComment,
      attachments: commentAttachments.length > 0 ? commentAttachments : undefined,
    };

    // Two requests - track which one failed so the toast can say whether the comment was saved
    let commentAdded = false;

    try {
      const comment = await addCommentMutation.mutateAsync({
        ticketId: ticket.id,
        comment: commentRequest,
      });
      commentAdded = true;
      const attachmentNote = missingAttachmentsNote(
        commentAttachments.length,
        comment.attachments?.length ?? 0
      );

      // Clear comment form
      setNewComment("");
      setIsInternalComment(false);
      setCommentAttachments([]);
      setFileUploadKey(`fileupload-${Date.now()}`);

      // Now update the status
      const status = parseInt(statusValue) as TicketStatus;
      await updateTicketMutation.mutateAsync({
        id: ticket.id,
        updates: {
          subject: ticket.subject,
          description: ticket.description,
          status,
          priority: ticket.priority,
          categoryId: ticket.category?.id || null,
          assignedToId: ticket.assignedTo?.id || null,
          dueAt: ticket.dueAt || null,
        },
      });

      const statusLabel = TicketStatusLabels[status];
      if (attachmentNote) {
        showToast.error(
          "Attachments Not Uploaded",
          `Comment added and status set to ${statusLabel}, but ${attachmentNote}.`
        );
      } else {
        showToast.success("Comment Added", `Comment added and status set to ${statusLabel}.`);
      }
    } catch (err) {
      console.error("Error adding comment with status:", err);
      showToast.error(
        "Action Failed",
        commentAdded
          ? `Your comment was added, but the status wasn't changed. ${getErrorMessage(err, "Failed to update ticket status")}`
          : getErrorMessage(err, "Failed to add comment")
      );
    }
  };

  // Build split button options based on current ticket status
  const commentStatusOptions = useMemo(() => {
    if (!ticket) return [];
    const statusEntries: { value: TicketStatus; label: string; icon: React.ReactNode }[] = [
      { value: TicketStatus.Open, label: "Add Comment & set Open", icon: <CircleDot className="h-4 w-4" /> },
      { value: TicketStatus.InProgress, label: "Add Comment & set In Progress", icon: <Hourglass className="h-4 w-4" /> },
      { value: TicketStatus.Pending, label: "Add Comment & set Pending", icon: <AlertCircle className="h-4 w-4" /> },
      { value: TicketStatus.OnHold, label: "Add Comment & set On Hold", icon: <PauseCircle className="h-4 w-4" /> },
      { value: TicketStatus.Resolved, label: "Add Comment & Resolve", icon: <CheckCircle className="h-4 w-4" /> },
    ];
    return statusEntries
      .filter(entry => entry.value !== ticket.status && entry.value !== TicketStatus.Closed)
      .map(entry => ({
        label: entry.label,
        value: entry.value.toString(),
        icon: entry.icon,
        ...(entry.value === TicketStatus.Resolved && resolveBlockedByCategory
          ? { disabled: true, hint: "Choose a category first" }
          : {}),
      }));
  }, [ticket, resolveBlockedByCategory]);

  const handleAddComment = async () => {
    if (!ticket || !newComment.trim()) return;

    const commentRequest: AddCommentRequest = {
      content: newComment.trim(),
      isInternal: isInternalComment,
      attachments: commentAttachments.length > 0 ? commentAttachments : undefined,
    };

    try {
      // Create the comment and upload its attachments in one request
      const comment = await addCommentMutation.mutateAsync({
        ticketId: ticket.id,
        comment: commentRequest,
      });

      // Report the attachments that were actually saved, not the ones that were sent
      const savedAttachments = comment.attachments?.length ?? 0;
      const attachmentNote = missingAttachmentsNote(commentAttachments.length, savedAttachments);
      if (attachmentNote) {
        showToast.error("Attachments Not Uploaded", `Your comment was added, but ${attachmentNote}.`);
      } else if (savedAttachments > 0) {
        showToast.success(
          "Comment Added",
          `Comment added successfully with ${savedAttachments} attachment(s).`
        );
      } else {
        showToast.commentAdded();
      }

      // Clear form
      setNewComment("");
      setIsInternalComment(false);
      setCommentAttachments([]);
      setFileUploadKey(`fileupload-${Date.now()}`); // Force FileUpload component to re-render and clear
    } catch (err) {
      console.error("Error adding comment:", err);
      showToast.error("Comment Failed", getErrorMessage(err, "Failed to add comment"));
    }
  };

  const handleDownloadAttachment = async (
    attachmentId: string,
    fileName: string
  ) => {
    try {
      const blob = await apiService.downloadAttachment(attachmentId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download attachment:", error);
      showToast.error("Download Failed", getErrorMessage(error, `Failed to download ${fileName}. Please try again.`));
    }
  };

  // Helper functions for status/priority icons
  const getStatusBadgeIcon = (status: TicketStatus) => {
    switch (status) {
      case TicketStatus.Open:
        return <CircleDot className="h-3 w-3" />;
      case TicketStatus.InProgress:
        return <Hourglass className="h-3 w-3" />;
      case TicketStatus.Pending:
        return <AlertCircle className="h-3 w-3" />;
      case TicketStatus.OnHold:
        return <PauseCircle className="h-3 w-3" />;
      case TicketStatus.Resolved:
        return <CheckCircle className="h-3 w-3" />;
      case TicketStatus.Closed:
        return <XCircle className="h-3 w-3" />;
      default:
        return <CircleDot className="h-3 w-3" />;
    }
  };

  const getPriorityIcon = (priority: Priority) => {
    switch (priority) {
      case Priority.Low:
        return <ArrowDown className="h-3 w-3" />;
      case Priority.Medium:
        return <Minus className="h-3 w-3" />;
      case Priority.High:
        return <ArrowUp className="h-3 w-3" />;
      case Priority.Critical:
        return <AlertTriangle className="h-3 w-3" />;
      default:
        return <Minus className="h-3 w-3" />;
    }
  };

  if (isLoading) {
    return <TicketDetailSkeleton />;
  }

  if (isError && !ticket) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error?.message || "Failed to load ticket"}
        </div>
        <div className="mt-4">
          <Link to={getPath.tickets()}>
            <Button variant="outline">← Back to Tickets</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            Ticket not found
          </h2>
          <Link to={getPath.tickets()}>
            <Button variant="outline">← Back to Tickets</Button>
          </Link>
        </div>
      </div>
    );
  }

  // A merged ticket is a closed stub: its conversation lives on the target, so
  // editing and re-merging are both off the table.
  const isMerged = !!ticket.mergedIntoTicketId;
  const mergedFromTickets = ticket.mergedFromTickets ?? [];

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 border-b border-gray-200 dark:border-gray-700 pb-3 sm:pb-4">
        <div className="flex items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
          <Link to={getPath.tickets()}>
            <Button variant="outline" size="icon" className="flex-shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg sm:text-2xl font-bold text-foreground break-words">
                {ticket.subject}
              </h1>
              <OrganizationBadge organizationName={ticket.organizationName} />
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Ticket #{ticket.ticketNumber} • Created{" "}
              {formatDateTime(ticket.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          {editMode ? (
            <>
              <Button
                onClick={handleUpdate}
                disabled={updateTicketMutation.isPending}
                className="w-full sm:w-auto"
              >
                <Save className="h-4 w-4 mr-2" />
                {updateTicketMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
              <Button variant="outline" onClick={handleEditModeToggle} className="w-full sm:w-auto">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </>
          ) : (
            <>
              <TicketFollowButton ticketId={id} organizationId={ticket?.organizationId} assignedToId={ticket.assignedTo?.id} variant="default" />
              <Button
                onClick={handleEditModeToggle}
                className="w-full sm:w-auto"
                disabled={isMerged}
                title={isMerged ? "Merged tickets are read-only" : undefined}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Ticket
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="flex-shrink-0">
                    <MoreVertical className="h-4 w-4" />
                    <span className="sr-only">More actions</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    // Deferred a tick so the menu finishes closing and restoring focus to
                    // the trigger before the dialog mounts - otherwise Radix's focus
                    // restore races the dialog's focus trap and the search input never
                    // receives autoFocus.
                    onSelect={() => setTimeout(() => setMergeDialogOpen(true), 0)}
                    disabled={isMerged}
                    className="cursor-pointer"
                  >
                    <GitMerge className="h-4 w-4 mr-2" />
                    Merge into another ticket...
                  </DropdownMenuItem>
                  {isTicketOrgAdmin && (
                    <DropdownMenuItem
                      onSelect={() => setTimeout(() => setTrashDialogOpen(true), 0)}
                      className="cursor-pointer text-red-600"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete ticket...
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>

      {/* Merged banner - this ticket was merged away */}
      {isMerged && (
        <div className="flex items-start gap-3 rounded-lg border border-indigo-200 dark:border-indigo-900 bg-indigo-50 dark:bg-indigo-950/40 p-3">
          <GitMerge className="h-4 w-4 text-indigo-600 dark:text-indigo-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm min-w-0">
            <p className="font-medium text-indigo-900 dark:text-indigo-200">
              This ticket was merged
              {ticket.mergedIntoTicketNumber ? ` into #${ticket.mergedIntoTicketNumber}` : ""}
              {ticket.mergedAt ? ` on ${formatDateTime(ticket.mergedAt)}` : ""}.
            </p>
            <p className="text-indigo-700 dark:text-indigo-300">
              Its conversation now lives on the other ticket and this one is read-only.
              {ticket.mergedIntoTicketId && (
                <>
                  {" "}
                  <Link
                    to={getPath.ticket(ticket.mergedIntoTicketId)}
                    className="underline underline-offset-2 font-medium"
                  >
                    Open ticket
                    {ticket.mergedIntoTicketNumber ? ` #${ticket.mergedIntoTicketNumber}` : ""}
                  </Link>
                </>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Merged-from chips - other tickets consolidated into this one */}
      {mergedFromTickets.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3">
          <GitMerge className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="text-sm min-w-0">
            <p className="font-medium text-foreground">
              {mergedFromTickets.length} ticket{mergedFromTickets.length === 1 ? " was" : "s were"} merged into this one
            </p>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {mergedFromTickets.map((m) => (
                <Link key={m.id} to={getPath.ticket(m.id)}>
                  <Badge variant="outline" className="hover:bg-accent cursor-pointer">
                    #{m.ticketNumber} {m.subject}
                  </Badge>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="flex justify-center py-2">
        <TicketProgressBar currentStatus={ticket.status} />
      </div>

      {/* Error Display */}
      {(updateTicketMutation.error ||
        addCommentMutation.error ||
        categoriesError ||
        usersError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {updateTicketMutation.error?.message ||
            addCommentMutation.error?.message ||
            (categoriesError && "Failed to load categories") ||
            (usersError && "Failed to load users")}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4 md:space-y-6">
          {/* Ticket Details */}
          <Card className="p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 transition-shadow hover:shadow-md">
            <h2 className="text-lg font-semibold mb-4">Ticket Details</h2>
            {editMode ? (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <FileText className="inline h-4 w-4 mr-1" />
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={editSubject}
                    onChange={handleSubjectChange}
                    placeholder="Ticket subject"
                    className={editErrors.subject ? "border-red-500" : ""}
                  />
                  {editErrors.subject && (
                    <p className="flex items-center gap-1 text-sm text-red-600 mt-1">
                      <AlertCircle className="h-4 w-4" />
                      {editErrors.subject}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <MessageSquare className="inline h-4 w-4 mr-1" />
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={editDescription}
                    onChange={handleDescriptionChange}
                    rows={6}
                    className={`w-full px-3 py-2 border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${
                      editErrors.description ? "border-red-500" : "border-input"
                    }`}
                    placeholder="Ticket description"
                  />
                  {editErrors.description && (
                    <p className="flex items-center gap-1 text-sm text-red-600 mt-1">
                      <AlertCircle className="h-4 w-4" />
                      {editErrors.description}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    Ticket Number & Subject
                  </label>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-semibold text-foreground">
                      #{ticket.ticketNumber}
                    </span>
                    <span className="text-muted-foreground">-</span>
                    <h3 className="text-lg font-semibold text-foreground">
                      {ticket.subject}
                    </h3>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-muted-foreground">
                      Description
                    </label>
                    {isTicketOrgAdmin && ticket.description && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-muted-foreground"
                        onClick={() => setRedactTarget({ content: ticket.description })}
                      >
                        <EyeOff className="h-3 w-3 mr-1" />
                        Redact
                      </Button>
                    )}
                  </div>
                  <div className="prose max-w-none dark:prose-invert">
                    <p className="text-foreground whitespace-pre-wrap">
                      {ticket.description}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Comments */}
          <Card className="p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700 transition-shadow hover:shadow-md">
            <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">
              Conversation
            </h3>

            <div className="max-h-[300px] sm:max-h-96 overflow-y-auto border rounded-md p-3 sm:p-4 bg-muted/20 scroll-smooth">
              {ticket.comments.length === 0 ? (
                <div className="flex items-center justify-center h-full text-center">
                  <div>
                    <div className="text-muted-foreground mb-2">💬</div>
                    <p className="text-muted-foreground">No comments yet</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Add the first comment below
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {ticket.comments.map((comment) => (
                    <div
                      key={comment.id}
                      className={`border-l-4 pl-2 sm:pl-4 py-2 sm:py-3 rounded-r-md bg-muted/30 ${
                        comment.isInternal
                          ? "border-yellow-400 dark:border-yellow-500"
                          : comment.isFromUser
                            ? "border-blue-400 dark:border-blue-500"
                            : "border-green-400 dark:border-green-500"
                      }`}
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-start mb-2">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="font-medium text-foreground text-sm sm:text-base">
                            {comment.authorName || 'System'}
                          </span>
                          <span className="text-xs sm:text-sm text-muted-foreground">
                            {comment.authorEmail}
                          </span>
                          {comment.isInternal && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                              Internal
                            </span>
                          )}
                          {comment.isSystemGenerated && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                              System
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
                            {formatDateTime(comment.createdAt)}
                          </span>
                          {isTicketOrgAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground"
                              onClick={() => setRedactTarget({ commentId: comment.id, content: comment.content })}
                              aria-label="Redact text from this comment"
                              title="Redact text"
                            >
                              <EyeOff className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm sm:text-base text-foreground whitespace-pre-wrap">
                        {comment.content}
                      </p>
                      {comment.attachments.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-muted">
                          <p className="text-xs font-medium text-muted-foreground mb-2">
                            Attachments:
                          </p>
                          <div className="space-y-1">
                            {comment.attachments.map((attachment) => (
                              <AttachmentItem
                                key={attachment.id}
                                attachment={attachment}
                                variant="compact"
                                onDownload={handleDownloadAttachment}
                                siblings={comment.attachments}
                                onDelete={isTicketOrgAdmin ? setAttachmentToDelete : undefined}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add Comment */}
            <div className="mt-6 pt-6 border-t border-border">
              <h4 className="text-md font-medium text-foreground mb-3">
                Add Comment
              </h4>
              <div className="space-y-3">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder={editMode ? "Save or cancel your edits before adding a comment" : "Type your comment here..."}
                  disabled={editMode}
                />

                <div className="mt-3">
                  <FileUpload
                    key={fileUploadKey}
                    onFilesChange={setCommentAttachments}
                    maxFiles={5}
                    maxSize={10}
                    disabled={editMode}
                  />
                </div>

                {editMode && (
                  <p className="flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
                    <AlertCircle className="h-4 w-4" />
                    Comments are disabled while editing. Save or cancel your changes first.
                  </p>
                )}

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isInternalComment}
                      onChange={(e) => setIsInternalComment(e.target.checked)}
                      className="rounded border-input text-primary focus:ring-ring flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={editMode}
                    />
                    <span className={`ml-2 text-xs sm:text-sm text-muted-foreground ${editMode ? "opacity-50" : ""}`}>
                      Internal comment (not visible to customer)
                    </span>
                  </label>

                  <SplitButton
                    label="Add Comment"
                    onClick={handleAddComment}
                    isPending={addCommentMutation.isPending || updateTicketMutation.isPending}
                    pendingLabel="Sending..."
                    disabled={editMode || !newComment.trim() || ticket.status === TicketStatus.Closed}
                    options={commentStatusOptions}
                    onOptionSelect={handleCommentWithStatus}
                    className="w-full sm:w-auto"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Ticket History */}
          <TicketHistory ticketId={ticket.id} />
        </div>

        {/* Sidebar */}
        <div className="space-y-4 md:space-y-6">
          {/* Ticket Info */}
          <Card className="p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700 transition-shadow hover:shadow-md">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Ticket Information
            </h3>
            <div className="space-y-4">
              {/* Status */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-blue-50/50 dark:bg-blue-950/50 flex items-center justify-center">
                    <Activity className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Status
                  </label>
                </div>
                <div className="ml-10">
                  {editMode ? (
                    <Select
                      value={editStatus.toString()}
                      onValueChange={(value) => setEditStatus(parseInt(value) as TicketStatus)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(TicketStatusLabels)
                          .filter(([value]) => {
                            const statusValue = parseInt(value) as TicketStatus;
                            // Filter out Closed status - only background service should close tickets
                            return statusValue !== TicketStatus.Closed;
                          })
                          .map(([value, label]) => {
                            const resolveNeedsCategory =
                              parseInt(value) === TicketStatus.Resolved && categoryRequiredToResolve && editCategoryId === "";
                            return (
                              <SelectItem key={value} value={value} disabled={resolveNeedsCategory}>
                                {label}
                                {resolveNeedsCategory && " (choose a category first)"}
                              </SelectItem>
                            );
                          })}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${TicketStatusColors[ticket.status]}`}
                    >
                      {getStatusBadgeIcon(ticket.status)}
                      {TicketStatusLabels[ticket.status]}
                    </span>
                  )}
                </div>
              </div>

              {/* Priority */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-purple-50/50 dark:bg-purple-950/50 flex items-center justify-center">
                    <Signal className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Priority
                  </label>
                </div>
                <div className="ml-10">
                  {editMode ? (
                    <Select
                      value={editPriority.toString()}
                      onValueChange={(value) => setEditPriority(parseInt(value) as Priority)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PriorityLabels).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${PriorityColors[ticket.priority]}`}
                    >
                      {getPriorityIcon(ticket.priority)}
                      {PriorityLabels[ticket.priority]}
                    </span>
                  )}
                </div>
              </div>

              {/* Category */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-green-50/50 dark:bg-green-950/50 flex items-center justify-center">
                    <FolderOpen className="h-4 w-4 text-green-600 dark:text-green-400" />
                  </div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Category
                  </label>
                </div>
                <div className="ml-10">
                  {editMode ? (
                    <>
                      <TreeSelect
                        options={categoryOptions}
                        value={editCategoryId || null}
                        onChange={(id) => setEditCategoryId(id ?? "")}
                        placeholder="Select category"
                        noneLabel="No category"
                        searchPlaceholder="Search categories..."
                        emptyMessage="No categories"
                      />
                      {categoryRequiredToResolve && editCategoryId === "" && (
                        <p className="mt-1 text-xs text-muted-foreground">Required to resolve this ticket</p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-900 dark:text-white">
                      {ticket.category?.path || ticket.category?.name || "No Category"}
                    </p>
                  )}
                </div>
              </div>

              {/* Assigned To */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-gray-50/50 dark:bg-gray-800/50 flex items-center justify-center">
                    <User className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  </div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Assigned To
                  </label>
                </div>
                <div className="ml-10">
                  {editMode ? (
                    <Select
                      value={editAssignedToId || "unassigned"}
                      onValueChange={(value) => setEditAssignedToId(value === "unassigned" ? "" : value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select assignee" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {assigneeOptions.map((user) => (
                          <SelectItem
                            key={user.id}
                            value={user.id}
                            className={!user.isAssignable ? "text-muted-foreground" : ""}
                          >
                            {user.fullName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm text-gray-900 dark:text-white">
                      {ticket.assignedTo
                        ? ticket.assignedTo.fullName
                        : "Unassigned"}
                    </p>
                  )}
                </div>
              </div>

              {/* Created */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-orange-50/50 dark:bg-orange-950/50 flex items-center justify-center">
                    <Calendar className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                  </div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Created
                  </label>
                </div>
                <p className="ml-10 text-sm text-gray-900 dark:text-white">
                  {formatDateTime(ticket.createdAt)}
                </p>
              </div>

              {/* Last Updated */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50/50 dark:bg-indigo-950/50 flex items-center justify-center">
                    <RefreshCw className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Last Updated
                  </label>
                </div>
                <p className="ml-10 text-sm text-gray-900 dark:text-white">
                  {formatDateTime(ticket.updatedAt)}
                </p>
              </div>

              {/* Due Date */}
              {(editMode || ticket.dueAt) && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-amber-50/50 dark:bg-amber-950/50 flex items-center justify-center">
                      <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Due Date
                    </label>
                  </div>
                  <div className="ml-10">
                    {editMode ? (
                      <div>
                        <DateTimePicker
                          value={editDueAt || undefined}
                          onChange={(value) => setEditDueAt(value || "")}
                          placeholder="Select due date"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Leave empty to remove due date
                        </p>
                      </div>
                    ) : (
                      <p
                        className={`text-sm font-medium ${ticket.isOverdue ? "text-red-600" : "text-gray-900 dark:text-white"}`}
                      >
                        {formatDateTime(ticket.dueAt)}
                        {ticket.isOverdue && (
                          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                            Overdue
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Resolved */}
              {ticket.resolvedAt && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-teal-50/50 dark:bg-teal-950/50 flex items-center justify-center">
                      <CheckCircle className="h-4 w-4 text-teal-600 dark:text-teal-400" />
                    </div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Resolved
                    </label>
                  </div>
                  <p className="ml-10 text-sm text-gray-900 dark:text-white">
                    {formatDateTime(ticket.resolvedAt)}
                  </p>
                </div>
              )}

              {/* SLA Metrics Section - Only show if there's meaningful SLA data */}
              {(ticket.firstResponseCalendarSeconds !== undefined ||
                ticket.netResolutionSeconds !== undefined) && (
                <>
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                    <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">
                      SLA Metrics
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* First Response */}
                    {ticket.firstResponseCalendarSeconds !== undefined && (
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-cyan-50/50 dark:bg-cyan-950/50 flex items-center justify-center flex-shrink-0">
                          <Clock className="h-3 w-3 text-cyan-600 dark:text-cyan-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500 dark:text-gray-400">First Response</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatDuration(ticket.firstResponseCalendarSeconds)}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Net Resolution */}
                    {ticket.netResolutionSeconds !== undefined && (
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-violet-50/50 dark:bg-violet-950/50 flex items-center justify-center flex-shrink-0">
                          <Clock className="h-3 w-3 text-violet-600 dark:text-violet-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Net Resolution</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatDuration(ticket.netResolutionSeconds)}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* First Response (Business Hours) - Only show if different from calendar */}
                    {ticket.firstResponseBusinessSeconds !== undefined &&
                      ticket.firstResponseBusinessSeconds !== ticket.firstResponseCalendarSeconds && (
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-emerald-50/50 dark:bg-emerald-950/50 flex items-center justify-center flex-shrink-0">
                          <Clock className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500 dark:text-gray-400">First Response (Business Hours)</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatDuration(ticket.firstResponseBusinessSeconds)}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Net Resolution (Business Hours) - Only show if different from calendar */}
                    {ticket.netResolutionBusinessSeconds !== undefined &&
                      ticket.netResolutionBusinessSeconds !== ticket.netResolutionSeconds && (
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-indigo-50/50 dark:bg-indigo-950/50 flex items-center justify-center flex-shrink-0">
                          <Clock className="h-3 w-3 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Net Resolution (Business Hours)</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatDuration(ticket.netResolutionBusinessSeconds)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Time in Status Section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                    <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                      Time in Status
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {/* Open */}
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-blue-50/50 dark:bg-blue-950/50 flex items-center justify-center flex-shrink-0">
                        <CircleDot className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Open</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatDuration(ticket.openTimeSeconds)}
                        </p>
                      </div>
                    </div>

                    {/* In Progress */}
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-yellow-50/50 dark:bg-yellow-950/50 flex items-center justify-center flex-shrink-0">
                        <Hourglass className="h-3 w-3 text-yellow-600 dark:text-yellow-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 dark:text-gray-400">In Progress</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatDuration(ticket.inProgressTimeSeconds)}
                        </p>
                      </div>
                    </div>

                    {/* Pending */}
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-orange-50/50 dark:bg-orange-950/50 flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="h-3 w-3 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Pending</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatDuration(ticket.pendingTimeSeconds)}
                        </p>
                      </div>
                    </div>

                    {/* On Hold */}
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-purple-50/50 dark:bg-purple-950/50 flex items-center justify-center flex-shrink-0">
                        <PauseCircle className="h-3 w-3 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 dark:text-gray-400">On Hold</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatDuration(ticket.onHoldTimeSeconds)}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

            </div>
          </Card>

          {/* Customer & Participants */}
          <Card className="p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700 transition-shadow hover:shadow-md">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Customer
            </h3>
            <div className="space-y-4">
              {/* Primary Customer */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-50/50 dark:bg-blue-950/50 flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {ticket.customer.fullName}
                    {ticket.customer.isArchived && (
                      <Badge variant="secondary" className="ml-2 align-middle">
                        Archived
                      </Badge>
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    {ticket.customer.email}
                  </p>
                </div>
              </div>

              {/* Participants (embedded in Customer section) */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <TicketParticipants ticketId={id} organizationId={ticket?.organizationId} embedded readOnly={isMerged} />
              </div>

            </div>
          </Card>

          {/* Linked Articles */}
          <TicketArticleLinks ticketId={id} organizationId={ticket?.organizationId} readOnly={isMerged} />

          {/* Attachments */}
          {ticket.attachments.length > 0 && (
            <Card className="p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700 transition-shadow hover:shadow-md">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Attachments
              </h3>
              <div className="space-y-2">
                {ticket.attachments.map((attachment) => (
                  <AttachmentItem
                    key={attachment.id}
                    attachment={attachment}
                    variant="full"
                    onDownload={handleDownloadAttachment}
                    siblings={ticket.attachments}
                    onDelete={isTicketOrgAdmin ? setAttachmentToDelete : undefined}
                  />
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {ticket.organizationId && (
        <TicketMergeDialog
          sourceTicketId={ticket.id}
          sourceTicketNumber={ticket.ticketNumber}
          organizationId={ticket.organizationId}
          open={mergeDialogOpen}
          onOpenChange={setMergeDialogOpen}
          onMerged={(targetTicketId) => navigateTo.ticket(targetTicketId)}
        />
      )}

      <TicketTrashDialog
        ticketId={ticket.id}
        ticketNumber={ticket.ticketNumber}
        open={trashDialogOpen}
        onOpenChange={setTrashDialogOpen}
        onDeleted={() => navigateTo.tickets()}
      />

      <RedactTextDialog
        ticketId={ticket.id}
        commentId={redactTarget?.commentId}
        content={redactTarget?.content ?? ""}
        open={redactTarget !== null}
        onOpenChange={(open) => !open && setRedactTarget(null)}
      />

      <AttachmentDeleteDialog
        ticketId={ticket.id}
        attachment={attachmentToDelete}
        onOpenChange={(open) => !open && setAttachmentToDelete(null)}
      />
    </div>
  );
}
